/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Type-aware "mixed values" detection for bulk-editing artifact metadata.
 *
 * The backend sets `fromArtifact.mixedValue = true` on a metadata element when
 * the artifacts it merged had differing values. That flag is honored by
 * FsiPropertiesInlineEditor to render `PROPERTIES_MIXED_VALUES`. However, the
 * backend has no visibility into local (unsaved) edits the user has made in
 * the UI. This utility replicates that detection on the client so that a
 * per-artifact local override on one selected row correctly propagates as
 * "mixed" when other selected rows have a different (or no) local override.
 */

import moment from 'moment'

export type MetadataType =
  | 'DATETIME'
  | 'STRING'
  | 'NUMBER'
  | 'SELECT_ONE'
  | 'SELECT_MULTI'
  | 'ARTIFACT'
  | 'SELECT_ONE_USER'
  | 'SELECT_MULTI_USER'
  | 'SELECT_ONE_GROUP'
  | 'SELECT_MULTI_GROUP'

export interface SavedMetadataEntry {
  metadataTemplateId: number
  value: unknown
}

export interface MetadataTemplate {
  id: number
  type: MetadataType
  /** Backend-merged value shown to the user before local overrides are applied. */
  value?: unknown
  /** Backend-provided mixed flag (already true when server saw a divergence). */
  fromArtifact?: { mixedValue?: boolean; value?: unknown }
}

export interface MixedValueResult {
  mixed: boolean
  /** When `mixed === false`, the single value shared by every selected entry. */
  commonValue?: unknown
}

const SCALAR_ID_TYPES = new Set<MetadataType>(['SELECT_ONE', 'SELECT_ONE_USER', 'SELECT_ONE_GROUP', 'ARTIFACT'])

const MULTI_TYPES = new Set<MetadataType>(['SELECT_MULTI', 'SELECT_MULTI_USER', 'SELECT_MULTI_GROUP'])

/** Empty-ish check: null, undefined, '', or empty array. */
function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

/** Normalize a possibly-nested user/group/option selection to its identifier. */
function toId(value: unknown): unknown {
  if (value && typeof value === 'object' && 'id' in (value as Record<string, unknown>)) {
    return (value as Record<string, unknown>).id
  }
  if (value && typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
    return (value as Record<string, unknown>).value
  }
  return value
}

/** Normalize a datetime-ish value to a UTC millisecond timestamp (or null). */
function toTimestamp(value: unknown): number | null {
  if (isEmpty(value)) return null
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  const m = moment(value as moment.MomentInput)
  return m.isValid() ? m.valueOf() : null
}

/** Safe scalar stringification: primitives directly, objects via JSON. */
function safeStringify(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  return String(value)
}

/** Order-agnostic equality for arrays of scalars/objects (by id). */
function arraysEqualAsSets(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false
  const norm = (arr: unknown[]) =>
    arr
      .map(toId)
      .map(safeStringify)
      .sort((x, y) => x.localeCompare(y))
  const na = norm(a)
  const nb = norm(b)
  for (let i = 0; i < na.length; i += 1) {
    if (na[i] !== nb[i]) return false
  }
  return true
}

/**
 * Type-aware equality for two metadata values. Treats all "empty" shapes
 * (`null`, `undefined`, `''`, `[]`) as equal for a given type.
 */
export function isSameMetadataValue(a: unknown, b: unknown, type: MetadataType): boolean {
  if (isEmpty(a) && isEmpty(b)) return true
  if (isEmpty(a) !== isEmpty(b)) return false

  switch (type) {
    case 'DATETIME':
      return toTimestamp(a) === toTimestamp(b)

    case 'NUMBER': {
      const na = typeof a === 'string' ? Number(a) : (a as number)
      const nb = typeof b === 'string' ? Number(b) : (b as number)
      if (Number.isNaN(na) && Number.isNaN(nb)) return true
      return na === nb
    }

    case 'STRING':
      return safeStringify(a) === safeStringify(b)

    case 'SELECT_MULTI':
    case 'SELECT_MULTI_USER':
    case 'SELECT_MULTI_GROUP':
      return arraysEqualAsSets(Array.isArray(a) ? a : [a], Array.isArray(b) ? b : [b])

    case 'SELECT_ONE':
    case 'SELECT_ONE_USER':
    case 'SELECT_ONE_GROUP':
    case 'ARTIFACT':
      return toId(a) === toId(b)

    default:
      return a === b
  }
}

/**
 * Resolve the effective value for a single template on a single entry.
 * If the entry has a local override for the template, that wins; otherwise the
 * backend-merged value on the template is used.
 */
function resolveEffectiveValue(
  template: MetadataTemplate,
  entryMetadata: SavedMetadataEntry[] | undefined
): { value: unknown; hadOverride: boolean } {
  const override = entryMetadata?.find((m) => m?.metadataTemplateId === template.id)
  if (override) return { value: override.value, hadOverride: true }
  return { value: template.value, hadOverride: false }
}

/**
 * Given all selected entries' locally-saved metadata plus the (backend-merged)
 * template list, decide per template whether the selection is "mixed".
 *
 * Rules:
 *  - If the backend already flagged `fromArtifact.mixedValue`, any entry that
 *    did NOT locally override the template is treated as mixed → result stays
 *    mixed unless every entry has an override AND all overrides agree.
 *  - Otherwise, all effective values (override or backend default) must agree
 *    under the type-aware comparator; any divergence → mixed.
 */
export function computeMixedValueFlags(
  templates: MetadataTemplate[],
  selectedEntriesMetadata: Array<SavedMetadataEntry[] | undefined>
): Map<number, MixedValueResult> {
  const result = new Map<number, MixedValueResult>()

  // No selection or single-artifact edit: nothing to mix so set mixed=false and return the backend value. but don't we use 'mixedValue' attribute instead of 'mixed'
  if (!Array.isArray(selectedEntriesMetadata) || selectedEntriesMetadata.length < 2) {
    templates.forEach((tpl) => result.set(tpl.id, { mixed: false, commonValue: tpl.value }))
    return result
  }

  // Multiple selected entries: check each template for divergence across the selection.
  for (const tpl of templates) {
    // If the backend already saw a divergence, we need to check if every entry has a local override. If not, we cannot claim a common value → mixed.
    const backendMixed = tpl.fromArtifact?.mixedValue === true

    const resolved = selectedEntriesMetadata.map((entryMeta) => resolveEffectiveValue(tpl, entryMeta))
    const everyEntryOverrode = resolved.every((r) => r.hadOverride)

    // If the backend already saw a divergence and at least one entry has no
    // local override, we cannot claim a common value for that entry → mixed.
    if (backendMixed && !everyEntryOverrode) {
      result.set(tpl.id, { mixed: true })
      continue
    }

    const [head, ...tail] = resolved
    const allEqual = tail.every((r) => isSameMetadataValue(head.value, r.value, tpl.type))

    if (allEqual) {
      result.set(tpl.id, { mixed: false, commonValue: head.value })
    } else {
      result.set(tpl.id, { mixed: true })
    }
  }

  return result
}

/** Empty value to substitute when a template is mixed, based on its type. */
export function emptyValueForType(type: MetadataType): unknown {
  if (MULTI_TYPES.has(type)) return []
  if (SCALAR_ID_TYPES.has(type)) return null
  return null
}
